import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import GameMatchup from '@/shared/components/GameMatchup';
import CameraConfigFormV2 from './CameraConfigFormV2';
import dayjs from 'dayjs';

interface GameDetails {
  uniqueId?: string;
  status: 'recorded' | 'live' | 'scheduled' | 'delayed';
  startTimestamp: string;
  endTimestamp: string;
  enableTracking: boolean;
  trackingId: string | null;
  trackingOffset: string;
  enableAudio: boolean;
  audioOffset: string;
  streamId: string;
  homeTeamColor: string;
  awayTeamColor: string;
  version?: number;
  awayTeam: any;
  homeTeam: any;
  gameCameras?: Array<{
    uniqueId: string;
    displayName?: string | null;
    reingestRequired?: boolean | null;
    reingestStreamId?: string | null;
    camIndex: number;
  }>;
}

interface AdminFormProps {
  gameDetails: GameDetails;
  handleGameDataChange: (updatedDetails: GameDetails) => void;
  handleClose: () => void;
}

const AdminGameForm: React.FC<AdminFormProps> = ({ gameDetails, handleGameDataChange }) => {
  const [formData, setFormData] = useState<GameDetails>(gameDetails);

  const [trackingIdNull, setTrackingIdNull] = useState<boolean>(formData.trackingId === null);

  const handleChange = (field: keyof GameDetails, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (field === 'trackingId') {
      setTrackingIdNull(value === null || value === '');
    }
  };

  const handleGameCameraChange = (
    gameCameraId: string,
    updates: Partial<NonNullable<GameDetails['gameCameras']>[number]>,
  ) => {
    setFormData((prev) => ({
      ...prev,
      gameCameras: prev.gameCameras?.map((gameCamera) =>
        gameCamera.uniqueId === gameCameraId ? { ...gameCamera, ...updates } : gameCamera,
      ),
    }));
  };

  const { homeTeam, awayTeam } = gameDetails;

  const homePrimaryPrimaryTeamColor =
    homeTeam.teamColors?.find((c: any) => c.type === 'primary')?.hex_color || '#000000';

  const homePrimarySecondaryTeamColor =
    homeTeam.teamColors?.find((c: any) => c.type === 'secondary')?.hex_color || '#000000';

  const awayPrimaryPrimaryTeamColor =
    awayTeam.teamColors?.find((c: any) => c.type === 'primary')?.hex_color || '#000000';

  const awayPrimarySecondaryTeamColor =
    awayTeam.teamColors?.find((c: any) => c.type === 'secondary')?.hex_color || '#000000';

  const [homeTeamSecondaryColorEnabled, setHomeTeamSecondaryColorEnabled] =
    useState<boolean>(false);
  const [awayTeamSecondaryColorEnabled, setAwayTeamSecondaryColorEnabled] =
    useState<boolean>(false);

  useEffect(() => {
    setFormData(gameDetails);
  }, [gameDetails]);

  useEffect(() => {
    handleGameDataChange(formData);
  }, [formData]);

  const handleSetHomeSecondaryColor = () => {
    setHomeTeamSecondaryColorEnabled((prev) => !prev);
    handleChange(
      'homeTeamColor',
      homeTeamSecondaryColorEnabled ? homePrimaryPrimaryTeamColor : homePrimarySecondaryTeamColor,
    );
  };

  const handleSetAwaySecondaryColor = () => {
    setAwayTeamSecondaryColorEnabled((prev) => !prev);
    handleChange(
      'awayTeamColor',
      awayTeamSecondaryColorEnabled ? awayPrimaryPrimaryTeamColor : awayPrimarySecondaryTeamColor,
    );
  };

  const starTimstamp = useMemo(() => {
    return formData.startTimestamp
      ? dayjs.isDayjs(formData.startTimestamp)
        ? formData.startTimestamp
        : dayjs(formData.startTimestamp)
      : null;
  }, [formData.startTimestamp]);

  const endTimestamp = useMemo(() => {
    return formData.endTimestamp
      ? dayjs.isDayjs(formData.endTimestamp)
        ? formData.endTimestamp
        : dayjs(formData.endTimestamp)
      : null;
  }, [formData.endTimestamp]);

  return (
    <Box component="div" sx={{ gap: 2, padding: 3, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ gap: 2, display: 'flex', flexDirection: 'row' }}>
        {/* First Column */}
        <Box sx={{ gap: 2, flex: '1', display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h6">General</Typography>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              variant="outlined"
              value={formData.status}
              onChange={(e) => handleChange('status', e.target.value)}
            >
              <MenuItem value="delayed" sx={{ color: 'orange' }}>
                Delayed
              </MenuItem>
              <MenuItem value="scheduled">Scheduled</MenuItem>

              <MenuItem value="live" sx={{ color: 'red' }}>
                Live
              </MenuItem>
              <MenuItem value="recorded">Recorded</MenuItem>
            </Select>
          </FormControl>
          <DateTimePicker
            label="Start Time"
            value={starTimstamp}
            onChange={(newValue) =>
              handleChange(
                'startTimestamp',
                newValue ? newValue.toISOString() : new Date(formData.startTimestamp),
              )
            }
          />
          <DateTimePicker
            label="End Time"
            value={endTimestamp}
            onChange={(newValue) =>
              handleChange('endTimestamp', newValue ? newValue.toISOString() : null)
            }
          />
          <TextField
            label="Stream ID"
            value={formData.streamId}
            onChange={(e) => handleChange('streamId', e.target.value)}
            fullWidth
            helperText="This is the stream ID for the game."
          />

          <Box sx={{ gap: 1, height: '200px', display: 'flex', flexDirection: 'column' }}>
            <FormControlLabel
              sx={{ m: 1 }}
              control={
                <Switch
                  checked={homeTeamSecondaryColorEnabled}
                  onClick={handleSetHomeSecondaryColor}
                />
              }
              label={`Use ${gameDetails.homeTeam.abbreviation} secondary color for this game`}
            />
            <FormControlLabel
              sx={{ m: 1 }}
              control={
                <Switch
                  checked={awayTeamSecondaryColorEnabled}
                  onClick={handleSetAwaySecondaryColor}
                />
              }
              label={`Use ${gameDetails.awayTeam.abbreviation} secondary color for this game`}
            />

            <GameMatchup game={formData} />
          </Box>
        </Box>

        {/* Second Column */}
        <Box sx={{ gap: 2, flex: '1', display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h6">Tracking and Audio</Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.enableTracking}
                onChange={(e) => handleChange('enableTracking', e.target.checked)}
              />
            }
            label="Enable Tracking"
          />
          <Box sx={{ gap: 1, display: 'flex', alignItems: 'center' }}>
            <TextField
              label="Tracking ID"
              value={formData.trackingId || ''}
              onChange={(e) => handleChange('trackingId', e.target.value)}
              fullWidth
              helperText="This is the SMT tracking ID for the game"
            />
          </Box>
          <TextField
            label="Tracking Offset"
            value={formData.trackingOffset}
            onChange={(e) => handleChange('trackingOffset', e.target.value)}
            fullWidth
            slotProps={{ htmlInput: { inputMode: 'numeric', pattern: '\\d*' } }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.enableAudio}
                onChange={(e) => handleChange('enableAudio', e.target.checked)}
              />
            }
            label="Enable Audio"
          />
          <TextField
            label="Audio Offset"
            value={formData.audioOffset}
            onChange={(e) => handleChange('audioOffset', e.target.value)}
            fullWidth
            slotProps={{ htmlInput: { inputMode: 'numeric', pattern: '\\d*' } }}
          />

          <Typography variant="h6">Reingest Configuration</Typography>
          <Box sx={{ gap: 2, display: 'flex', flexDirection: 'column' }}>
            {formData.gameCameras?.length ? (
              formData.gameCameras.map((gameCamera) => {
                const camIndex = gameCamera.camIndex ?? 0;
                const cameraName =
                  gameCamera.displayName ||
                  `Camera ${camIndex + 1}`;
                const reingestRequired = gameCamera.reingestRequired ?? false;

                return (
                  <Box key={gameCamera.uniqueId} sx={{ gap: 1, display: 'flex', flexDirection: 'column' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={reingestRequired}
                          onChange={(e) =>
                            handleGameCameraChange(gameCamera.uniqueId, {
                              reingestRequired: e.target.checked,
                              reingestStreamId: e.target.checked
                                ? gameCamera.reingestStreamId ?? gameDetails.streamId
                                : '',
                            })
                          }
                        />
                      }
                      label={`${cameraName} (Cam ${camIndex + 1})`}
                    />
                    <TextField
                      label="Stream ID"
                      value={gameCamera.reingestStreamId ?? gameDetails.streamId}
                      onChange={(e) =>
                        handleGameCameraChange(gameCamera.uniqueId, {
                          reingestStreamId: e.target.value,
                        })
                      }
                      fullWidth
                      disabled={!reingestRequired}
                      helperText="Stream ID override for reingest"
                    />
                  </Box>
                );
              })
            ) : (
              <Typography variant="body2" color="text.secondary">
                No cameras available for reingest configuration.
              </Typography>
            )}
          </Box>
        </Box>

        {/* Third Column */}
        <Box sx={{ gap: 2, flex: '1', display: 'flex', flexDirection: 'column' }}>
          {gameDetails.uniqueId ? (
            <CameraConfigFormV2
              gameId={gameDetails.uniqueId}
              onCamerasChange={() => {
              }}
            />
          ) : (
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" color="error">
                Camera Configuration Not Available
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This game doesn't have a unique ID. Camera configuration is only available for games
                with proper identifiers.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default AdminGameForm;
